import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'
import Spinner from '../components/Spinner'

type InquiryListItem = {
  id: number
  user_id: number
  category: string
  title: string
  status: string
  created_at: string
}

type InquiryReply = {
  id: number
  inquiry_id: number
  admin_user_id: number | null
  content: string
  created_at: string
}

type InquiryDetail = {
  id: number
  user_id: number
  category: string
  title: string
  content: string
  status: string
  created_at: string
  updated_at: string
  replies: InquiryReply[]
}

const STATUSES = ['대기', '처리중', '완료'] as const
type Status = typeof STATUSES[number]

const STATUS_BADGE: Record<string, string> = {
  '대기': 'bg-[var(--color-surface)] text-[var(--color-text-tertiary)] border border-[var(--color-border-default)]',
  '처리중': 'bg-[var(--color-accent)]/10 text-[var(--color-accent)] border border-[var(--color-accent)]/30',
  '완료': 'bg-[var(--color-success)]/10 text-[var(--color-success)] border border-[var(--color-success)]/30',
}

const CATEGORY_ICON: Record<string, string> = {
  '문의': '❓',
  '건의': '💡',
  '버그신고': '🐛',
}

const CATEGORIES = ['전체', '문의', '건의', '버그신고'] as const
const STATUS_FILTERS = ['전체', ...STATUSES] as const

function formatDate(iso: string) {
  const d = new Date(iso)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

function formatDateTime(iso: string) {
  const d = new Date(iso)
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// ─── 상세 패널 ───────────────────────────────────────────────────────────────
function DetailPanel({
  item,
  onBack,
  onUpdated,
}: {
  item: InquiryListItem
  onBack: () => void
  onUpdated: (updated: InquiryListItem) => void
}) {
  const [detail, setDetail] = useState<InquiryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [replying, setReplying] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)

  useEffect(() => {
    loadDetail()
  }, [item.id])

  const loadDetail = async () => {
    setLoading(true)
    try {
      const { data } = await client.get<InquiryDetail>(`/inquiries/${item.id}`)
      setDetail(data)
    } catch {
      toast('상세 내용을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (newStatus: Status) => {
    if (!detail || detail.status === newStatus) return
    setStatusLoading(true)
    try {
      const { data } = await client.patch<InquiryListItem>(
        `/admin/inquiries/${item.id}/status`,
        { status: newStatus }
      )
      setDetail((prev) => prev ? { ...prev, status: data.status } : prev)
      onUpdated({ ...item, status: data.status })
      toast('상태가 변경되었습니다.', 'success')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '상태 변경에 실패했습니다.', 'error')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleReply = async () => {
    if (!replyText.trim()) { toast('답변 내용을 입력해주세요.', 'error'); return }
    setReplying(true)
    try {
      const { data } = await client.post<InquiryReply>(
        `/admin/inquiries/${item.id}/reply`,
        { content: replyText.trim() }
      )
      setDetail((prev) =>
        prev ? { ...prev, status: '처리중', replies: [...prev.replies, data] } : prev
      )
      onUpdated({ ...item, status: '처리중' })
      setReplyText('')
      toast('답변이 등록되었습니다.', 'success')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '답변 등록에 실패했습니다.', 'error')
    } finally {
      setReplying(false)
    }
  }

  const inputCls =
    'w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors text-sm resize-none'

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">문의 상세</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : detail ? (
        <>
          {/* 본문 */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span>{CATEGORY_ICON[detail.category] ?? ''}</span>
                <span className="text-xs text-[var(--color-text-tertiary)]">{detail.category}</span>
                <span className="text-xs text-[var(--color-text-disabled)]">UID {detail.user_id}</span>
              </div>
              <span className="text-xs text-[var(--color-text-disabled)] flex-shrink-0">{formatDateTime(detail.created_at)}</span>
            </div>
            <h3 className="text-[var(--color-text-primary)] font-semibold text-base mb-3">{detail.title}</h3>
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">{detail.content}</p>
          </div>

          {/* 상태 변경 */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
            <h3 className="text-[var(--color-text-primary)] font-semibold text-sm mb-3">상태 변경</h3>
            <div className="flex gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={statusLoading || detail.status === s}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors disabled:cursor-default ${
                    detail.status === s
                      ? STATUS_BADGE[s]
                      : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)] hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* 기존 답변 */}
          {detail.replies.length > 0 && (
            <div className="space-y-3">
              {detail.replies.map((reply) => (
                <div key={reply.id} className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[var(--color-accent)]">📢 운영자 답변</span>
                    <span className="text-xs text-[var(--color-text-disabled)]">{formatDateTime(reply.created_at)}</span>
                  </div>
                  <p className="text-sm text-[var(--color-text-primary)] leading-relaxed whitespace-pre-wrap">{reply.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* 답변 입력 */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
            <h3 className="text-[var(--color-text-primary)] font-semibold text-sm mb-3">
              {detail.replies.length > 0 ? '추가 답변 작성' : '답변 작성'}
            </h3>
            <textarea
              className={inputCls}
              rows={5}
              placeholder="답변 내용을 입력하세요."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              maxLength={2000}
            />
            <p className="text-right text-xs text-[var(--color-text-disabled)] mt-1">{replyText.length} / 2000</p>
            <button
              onClick={handleReply}
              disabled={replying}
              className="w-full mt-3 py-2.5 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity"
            >
              {replying ? '등록 중...' : '답변 등록'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────
export default function AdminInquiries() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [list, setList] = useState<InquiryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<InquiryListItem | null>(null)
  const [catFilter, setCatFilter] = useState<string>('전체')
  const [statusFilter, setStatusFilter] = useState<string>('전체')

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login', { replace: true }); return }
    if (user && !user.is_admin) { navigate('/dashboard', { replace: true }); return }
    if (user) loadList()
  }, [isAuthenticated, user])

  const loadList = async () => {
    setLoading(true)
    try {
      const { data } = await client.get<InquiryListItem[]>('/admin/inquiries')
      setList(data)
    } catch {
      toast('목록을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdated = (updated: InquiryListItem) => {
    setList((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
    if (selected?.id === updated.id) setSelected(updated)
  }

  const filtered = list.filter((item) => {
    if (catFilter !== '전체' && item.category !== catFilter) return false
    if (statusFilter !== '전체' && item.status !== statusFilter) return false
    return true
  })

  if (selected) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
        <div className="max-w-lg mx-auto">
          <DetailPanel
            item={selected}
            onBack={() => setSelected(null)}
            onUpdated={handleUpdated}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">문의 관리</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">관리자 — 전체 문의 목록</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/admin/usage"
              className="text-xs text-[var(--color-text-tertiary)] bg-[var(--color-surface)] border border-[var(--color-border-default)] px-2.5 py-1 rounded-full font-medium hover:border-[var(--color-border-accent)] transition-colors"
            >
              사용량
            </Link>
            <span className="text-xs text-[var(--color-accent)] bg-[var(--color-accent)]/10 px-2.5 py-1 rounded-full font-medium">
              총 {filtered.length}건
            </span>
          </div>
        </div>

        {/* 필터 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4 space-y-3">
          <div>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-2">카테고리</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCatFilter(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    catFilter === cat
                      ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]'
                      : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-2">상태</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    statusFilter === s
                      ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]'
                      : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 목록 */}
        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-10 text-center">
            <p className="text-4xl mb-3">📭</p>
            <p className="text-[var(--color-text-tertiary)] text-sm">해당 조건의 문의가 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4 hover:border-[var(--color-border-accent)] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span>{CATEGORY_ICON[item.category] ?? ''}</span>
                      <span className="text-xs text-[var(--color-text-tertiary)]">{item.category}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[item.status] ?? STATUS_BADGE['대기']}`}>
                        {item.status}
                      </span>
                      <span className="text-xs text-[var(--color-text-disabled)]">UID {item.user_id}</span>
                    </div>
                    <p className="text-[var(--color-text-primary)] text-sm font-medium truncate">{item.title}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1 text-[var(--color-text-disabled)]">
                    <span className="text-xs">{formatDate(item.created_at)}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
